import { FORM_ITEM_TYPES } from '@sportninja/common/hooks/useCustomForm'
import { QuestionType } from '../constants/typeDisplayLabels'

/**
 * Single source of truth for translating between the builder's legacy question
 * type strings and the API's form item type ids.
 *
 * This replaces six hand-written switch statements (two module-level pairs in
 * TeamCustomForm / PlayerCustomForm plus two nested shadows inside
 * TeamCustomForm's add/update handlers). Each of those omitted a different
 * subset of types while QuestionTypeSelector exposed all of them in both
 * builders, so picking e.g. "Player Relationship" in the team builder silently
 * saved a Short Answer field instead.
 *
 * Typing the forward map as Record<QuestionType, number> makes it exhaustive:
 * adding a type to typeDisplayLabels without mapping it here is a compile
 * error rather than a silent fallback to Short Answer. The reverse map is
 * derived from the forward one, so the two directions cannot drift.
 */
export const QUESTION_TYPE_TO_FORM_ITEM_TYPE_ID: Record<QuestionType, number> =
  {
    text: FORM_ITEM_TYPES.TEXT_FIELD,
    textarea: FORM_ITEM_TYPES.TEXT_AREA,
    number: FORM_ITEM_TYPES.NUMBER_FIELD,
    dropdown: FORM_ITEM_TYPES.DROPDOWN,
    checkbox: FORM_ITEM_TYPES.CHECKBOX,
    date: FORM_ITEM_TYPES.DATE,
    birthdate: FORM_ITEM_TYPES.BIRTH_DATE,
    phone: FORM_ITEM_TYPES.PHONE,
    email: FORM_ITEM_TYPES.EMAIL,
    url: FORM_ITEM_TYPES.URL,
    country: FORM_ITEM_TYPES.COUNTRY,
    player_position: FORM_ITEM_TYPES.PLAYER_POSITION,
    hockey_usa: FORM_ITEM_TYPES.HOCKEY_USA,
    player_number: FORM_ITEM_TYPES.PLAYER_NUMBER,
    relationship: FORM_ITEM_TYPES.RELATIONSHIP,
    teamOfficialRole: FORM_ITEM_TYPES.TEAM_OFFICIAL_ROLE,
  }

const FORM_ITEM_TYPE_ID_TO_QUESTION_TYPE: Record<number, QuestionType> =
  Object.entries(QUESTION_TYPE_TO_FORM_ITEM_TYPE_ID).reduce(
    (acc, [questionType, typeId]) => {
      acc[typeId] = questionType as QuestionType
      return acc
    },
    {} as Record<number, QuestionType>
  )

/**
 * Maps a builder question type to the API form item type id.
 * Unknown types fall back to Short Answer, matching the previous behaviour.
 */
export const getFormItemTypeId = (questionType: string): number =>
  QUESTION_TYPE_TO_FORM_ITEM_TYPE_ID[questionType as QuestionType] ??
  FORM_ITEM_TYPES.TEXT_FIELD

/**
 * Maps an API form item type id back to the builder question type.
 * Unknown ids fall back to Short Answer, matching the previous behaviour.
 */
export const getQuestionType = (typeId: number): QuestionType =>
  FORM_ITEM_TYPE_ID_TO_QUESTION_TYPE[typeId] ?? 'text'
